import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import _ from "lodash";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { getStatusAction } from "../../../store/reducers/app";
import { openModal } from "../../../store/reducers/modalConductor";
import { updateKOMatchPickAction } from "../../../store/reducers/picks";
import {
  getKnockoutGamesAction,
  getResultsKOAction,
} from "../../../store/reducers/results";
import { useContestId } from "../../../hooks/useContestId";
import { TabSwitch } from "../../customComponents";
import ContentWrapper from "../../Layout/ContentWrapper";
import Spinner from "../../Layout/Spinner";

// Bracket columns, R32 first (display-only feeders) → Final. Picks START at R16:
// the user never picks an R32 winner. An R16 match's candidate pool is the four
// teams in its two feeder R32 games; each later round's pool is the two winners
// the user already advanced from the feeding matches. Accept both the short enum
// (real data) and spelled-out names (legacy/demo).
const KO_ROUND_DEFS = [
  { keys: ["R32", "Round of 32"], label: "Round of 32", pickable: false },
  { keys: ["R16", "Round of 16"], label: "Round of 16", pickable: true },
  { keys: ["QF", "Quarterfinals", "Quarter-finals"], label: "Quarterfinals", pickable: true },
  { keys: ["SF", "Semifinals", "Semi-finals"], label: "Semifinals", pickable: true },
  { keys: ["F", "Final", "Finals"], label: "Final", pickable: true },
];
const KO_THIRD_KEYS = ["3rd", "Third Place", "3rd Place", "Third-place"];

// A small soccer ball that bounces + spins, shown on the team you just tapped
// while its pick is in flight — so the tap feels instant even though the winner
// only turns into a check once the server round-trip returns.
const BallSpinner = ({ size = 16 }) => (
  <Box
    aria-label="saving pick"
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      display: "inline-flex",
      animation: "wcBallBounce 0.7s ease-in-out infinite",
      "@keyframes wcBallBounce": {
        "0%": { transform: "translateY(0) rotate(0deg)" },
        "50%": { transform: "translateY(-5px) rotate(180deg)" },
        "100%": { transform: "translateY(0) rotate(360deg)" },
      },
    }}
  >
    <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
      <circle cx="16" cy="16" r="15" fill="#ffffff" stroke="#0f172a" strokeWidth="2" />
      <polygon points="16,9 21.7,13.1 19.5,19.8 12.5,19.8 10.3,13.1" fill="#0f172a" />
      <path
        d="M16 1.5 L16 9 M30.5 12 L21.7 13.1 M24.5 29 L19.5 19.8 M7.5 29 L12.5 19.8 M1.5 12 L10.3 13.1"
        stroke="#0f172a"
        strokeWidth="1.6"
        fill="none"
      />
    </svg>
  </Box>
);

// Order each column top-to-bottom by walking the bracket tree via
// winner_to_match_number (final → R32) so feeder pairs are adjacent and the
// connectors nest without crossing — same approach as the Results bracket.
// Falls back to numeric order if the wiring is incomplete.
const orderRoundsByBracket = (rs) => {
  if (rs.length < 2) return rs;
  const ordered = rs.map(() => []);
  let wiringComplete = true;
  const kidsFromRound = (parentMn, round) =>
    round.matches
      .filter((c) => Number(c.winner_to_match_number) === parentMn)
      .sort((a, b) => Number(a.match_number) - Number(b.match_number));
  const walk = (match, ri) => {
    ordered[ri].push(match);
    if (ri === 0) return;
    const kids = kidsFromRound(Number(match.match_number), rs[ri - 1]);
    if (kids.length !== 2) {
      wiringComplete = false;
      return;
    }
    kids.forEach((k) => walk(k, ri - 1));
  };
  [...rs[rs.length - 1].matches]
    .sort((a, b) => Number(a.match_number) - Number(b.match_number))
    .forEach((root) => walk(root, rs.length - 1));
  const placed = ordered.reduce((n, arr) => n + arr.length, 0);
  const total = rs.reduce((n, r) => n + r.matches.length, 0);
  if (!wiringComplete || placed !== total) return rs;
  return rs.map((r, ri) => ({ ...r, matches: ordered[ri] }));
};

const KnockoutPicks = (props) => {
  const {
    isKnockout,
    groupProgress,
    stickyTopOffset = 0,
    tabs,
    currTab,
    handleTabChange,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const currLocation = useLocation();
  const dispatch = useDispatch();
  const contestId = useContestId();

  const userId = useSelector((s) => s.app?.userId);
  const picksProgress = useSelector((s) => s.app?.picksProgress);
  const statusLoading = useSelector((s) => s.app?.loading);
  const picksLoading = useSelector((s) => s.picks?.loading);
  const koPicks = useSelector((s) => s.picks?.koPicks); // per-match winners
  const knockoutGames = useSelector((s) => s.results?.knockoutGames); // wiring + R32 teams
  const knockoutResults = useSelector((s) => s.results?.knockoutResults); // resolved games (actual outcomes)

  // The bracket skeleton (all matches incl. wiring + the R32 team data) comes
  // from /api/knockout_games; the per-match winners come from koPicks (loaded by
  // the parent's getAllPicks). Pull the skeleton here.
  useEffect(() => {
    dispatch(getKnockoutGamesAction({ contestId }));
  }, [dispatch, contestId]);

  // Also pull the resolved knockout results so we can grey out teams that have
  // actually been knocked out. A couple keeps one team active until BOTH of its
  // teams have lost; eliminations come from real match outcomes, not picks.
  useEffect(() => {
    if (!userId) return;
    dispatch(getResultsKOAction({ userId, contestId }));
  }, [dispatch, userId, contestId]);

  // keep the legacy group-route redirect
  useEffect(() => {
    if (currLocation?.pathname?.includes("group")) navigate("/picks/knockout");
  }, [currLocation, navigate]);

  // A World Cup contest gates the knockout bracket behind group-stage
  // completion; a Knockout contest has no group stage (R32 is fixed at setup).
  useEffect(() => {
    if (!isKnockout && !picksProgress?.groupStageComplete) {
      dispatch(
        openModal({
          modalType: "KO_LOCKED_MODAL",
          modalProps: { show: true, groupsCompleted: groupProgress },
        }),
      );
    }
  }, [isKnockout, picksProgress, groupProgress, dispatch]);

  // Serialize taps. ko_picks mirrors into the simple model + recalculates status
  // server-side; firing a second tap before the first returns can interleave
  // those writes, so gate on a synchronous ref (immune to React batching).
  const isUpdatingRef = useRef(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // which team-tap is currently round-tripping, so we can show a busy ball on it
  const [pendingPick, setPendingPick] = useState(null);
  // whether the bracket can still scroll right — drives the right-edge "more →"
  // affordance; goes false once scrolled to the end so the cue disappears.
  const [canScrollRight, setCanScrollRight] = useState(false);

  // The bracket scrolls horizontally in its own overflow-x container; we read
  // its scroll position to toggle the right-edge "more →" cue.
  const bracketScrollRef = useRef(null);

  // team_number -> { teamNumber, name, flag, worldRank }. Sourced from the R32
  // matches in the skeleton — those carry every team that can appear in any
  // knockout round (later-round slots are TBD until results resolve them).
  const teamInfo = useMemo(() => {
    const map = {};
    (knockoutGames || []).forEach((m) => {
      [
        ["team_1_number", "team_1_name", "team_1_flag", "team_1_world_rank"],
        ["team_2_number", "team_2_name", "team_2_flag", "team_2_world_rank"],
      ].forEach(([nk, namek, fk, rk]) => {
        const n = Number(m[nk]);
        if (n > 0 && !map[n]) {
          map[n] = { teamNumber: n, name: m[namek], flag: m[fk], worldRank: m[rk] };
        }
      });
    });
    return map;
  }, [knockoutGames]);

  // match_number -> winner_team_number the user picked (>0 only)
  const picksByMatch = useMemo(() => {
    const map = {};
    (koPicks || []).forEach((p) => {
      const mn = Number(p.match_number);
      const w = Number(p.winner_team_number);
      if (mn && w > 0) map[mn] = w;
    });
    return map;
  }, [koPicks]);

  // Teams actually knocked out = the non-winner of any RESOLVED knockout match
  // (winner_team_number > 0). A couple greys one team once it loses; when both
  // of a couple's teams are in this set the whole couple reads as out.
  const eliminatedTeams = useMemo(() => {
    const out = new Set();
    (knockoutResults || []).forEach((m) => {
      const w = Number(m.winner_team_number);
      if (!(w > 0)) return; // unresolved match
      const t1 = Number(m.team_1_number);
      const t2 = Number(m.team_2_number);
      if (t1 > 0 && t1 !== w) out.add(t1);
      if (t2 > 0 && t2 !== w) out.add(t2);
    });
    return out;
  }, [knockoutResults]);

  // bracket columns, ordered so feeder pairs sit adjacent
  const rounds = useMemo(() => {
    const raw = KO_ROUND_DEFS.map((def) => ({
      def,
      matches: _.sortBy(
        (knockoutGames || []).filter((m) => def.keys.includes(m.round_name)),
        (m) => Number(m.match_number),
      ),
    })).filter((r) => r.matches.length > 0);
    return orderRoundsByBracket(raw);
  }, [knockoutGames]);

  const thirdMatch = useMemo(
    () => (knockoutGames || []).find((m) => KO_THIRD_KEYS.includes(m.round_name)),
    [knockoutGames],
  );

  // ---- couples (play-in style) ----
  // Each Round-of-32 matchup is a "couple": its two teams advance together. From
  // the Round of 16 on, the user picks one of TWO couples (not one of four
  // teams). A couple is tracked by a representative team — the R32 match's
  // team_1 — which is what we store in ko_picks, so the existing one-winner-per-
  // match storage, submit gate, and simple-model projection are all unchanged.
  const couples = useMemo(() => {
    const list = [];
    (rounds[0]?.matches || []).forEach((m) => {
      const t1 = Number(m.team_1_number);
      const t2 = Number(m.team_2_number);
      if (t1 > 0 || t2 > 0) {
        const teams = [t1, t2].filter((n) => n > 0);
        list.push({ id: Number(m.match_number), rep: teams[0], teams });
      }
    });
    return list;
  }, [rounds]);

  const coupleById = useMemo(() => {
    const map = {};
    couples.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [couples]);

  const coupleByRep = useMemo(() => {
    const map = {};
    couples.forEach((c) => {
      map[Number(c.rep)] = c;
    });
    return map;
  }, [couples]);

  // the two display teams for a Round-of-32 matchup card
  const matchupTeams = useCallback(
    (match) =>
      [match.team_1_number, match.team_2_number]
        .map(Number)
        .filter((n) => n > 0),
    [],
  );

  // the two candidate COUPLES for a pickable match (R16 → Final). At R16 the two
  // candidates are the couples of the two feeder R32 matchups; at later rounds
  // they are the couples the user already advanced out of the two feeding
  // matches (looked up by the stored representative team).
  const coupleCandidatesFor = useCallback(
    (match, ri) => {
      if (ri < 1) return [];
      const mn = Number(match.match_number);
      const prev = rounds[ri - 1]?.matches || [];
      let feeders = prev.filter((f) => Number(f.winner_to_match_number) === mn);
      if (feeders.length === 0) {
        const idx = rounds[ri].matches.findIndex(
          (x) => Number(x.match_number) === mn,
        );
        feeders = [prev[idx * 2], prev[idx * 2 + 1]].filter(Boolean);
      }
      if (ri === 1) {
        // R16: each feeder R32 matchup IS a couple
        return feeders
          .map((f) => coupleById[Number(f.match_number)])
          .filter(Boolean);
      }
      // QF/SF/F: the couple advanced from each feeder match (by its rep team)
      return feeders
        .map((f) => coupleByRep[Number(picksByMatch[Number(f.match_number)])])
        .filter(Boolean);
    },
    [rounds, picksByMatch, coupleById, coupleByRep],
  );

  // 3rd-place candidates = the two SF losing COUPLES (each SF match's non-picked
  // candidate couple). Empty until both SF matches have a pick.
  const thirdCoupleCandidates = useMemo(() => {
    const sfIdx = rounds.findIndex((r) => r.def.keys.includes("SF"));
    if (sfIdx < 0) return [];
    return rounds[sfIdx].matches
      .map((sf) => {
        const cands = coupleCandidatesFor(sf, sfIdx); // the two QF-winner couples
        const pickRep = Number(picksByMatch[Number(sf.match_number)]);
        return cands.find((c) => Number(c.rep) !== pickRep);
      })
      .filter(Boolean);
  }, [rounds, coupleCandidatesFor, picksByMatch]);

  // ---- pick handler ----
  const onPick = useCallback(
    async (matchNumber, teamNumber) => {
      if (isUpdatingRef.current) return;
      // Toggle: tapping the team that's already selected UNPICKS it. Unpick is a
      // ko_picks write with winner_team_number = -1 — the controller clears the
      // previous winner's advancement flag in user_knockout_simple and resets
      // the match winner to the provisioned "no pick" sentinel (the "set new
      // winner" step then no-ops, since no team -1 exists). Tapping a different
      // team switches the pick.
      const current = picksByMatch[Number(matchNumber)];
      const nextWinner =
        Number(current) === Number(teamNumber) ? -1 : Number(teamNumber);
      isUpdatingRef.current = true;
      setIsUpdating(true);
      setPendingPick({
        matchNumber: Number(matchNumber),
        teamNumber: Number(teamNumber),
      });
      try {
        const res = await dispatch(
          updateKOMatchPickAction({
            userId,
            contestId,
            matchNumber: Number(matchNumber),
            winnerTeamNumber: nextWinner,
          }),
        );
        if (res?.meta?.requestStatus === "fulfilled") {
          // keep the parent's Submit button in sync (recalculate_status ran)
          dispatch(getStatusAction({ userId, contestId }));
        }
      } catch (err) {
        console.error("Error updating KO match pick: ", err);
      } finally {
        isUpdatingRef.current = false;
        setIsUpdating(false);
        setPendingPick(null);
      }
    },
    [dispatch, userId, contestId, picksByMatch],
  );

  // ---- layout geometry (variable card heights; connectors join card centers) ----
  const COL_W = isMobile ? 130 : 220;
  const COL_GAP = isMobile ? 12 : 52;
  const ROW_H = 28; // candidate / team row
  const CARD_HEAD = 18; // tiny match-id header
  const CARD_VPAD = 10; // total vertical padding
  const SLOT = isMobile ? 104 : 112; // leaf (R32) vertical pitch
  const HEADER_H = 26; // column-label band

  const ROW_GAP = 2; // matches the card column's gap
  const DIVIDER_H = 5; // divider between the two couple options

  // Card heights are fixed per round type so the bracket geometry never shifts
  // as picks change:
  //   - R32 matchup card: the two teams, one per row.
  //   - pickable card (R16 → Final, and 3rd): two couple options, each a single
  //     "Name/Name" row, with a divider between them.
  const MATCHUP_CARD_H = CARD_VPAD + CARD_HEAD + 2 * ROW_H + 2 * ROW_GAP;
  const PICK_CARD_H = CARD_VPAD + CARD_HEAD + 2 * ROW_H + DIVIDER_H + 3 * ROW_GAP;
  const cardHFor = (ri) => (ri === 0 ? MATCHUP_CARD_H : PICK_CARD_H);

  // per-match: candidates, height, centerY (leaves spaced by SLOT; parents
  // centered on the average of their feeders' centers).
  const layout = useMemo(() => {
    const info = {};
    rounds.forEach((round, ri) => {
      round.matches.forEach((m) => {
        const mn = Number(m.match_number);
        info[mn] = { ri, h: cardHFor(ri), centerY: null };
      });
    });
    (rounds[0]?.matches || []).forEach((m, i) => {
      const mn = Number(m.match_number);
      info[mn].centerY = HEADER_H + i * SLOT + info[mn].h / 2;
    });
    for (let ri = 1; ri < rounds.length; ri++) {
      rounds[ri].matches.forEach((m, mi) => {
        const mn = Number(m.match_number);
        const prev = rounds[ri - 1].matches;
        let kids = prev.filter((c) => Number(c.winner_to_match_number) === mn);
        if (kids.length === 0) kids = [prev[mi * 2], prev[mi * 2 + 1]].filter(Boolean);
        const ys = kids
          .map((c) => info[Number(c.match_number)]?.centerY)
          .filter((v) => v != null);
        info[mn].centerY = ys.length
          ? ys.reduce((a, b) => a + b, 0) / ys.length
          : HEADER_H + mi * SLOT + info[mn].h / 2;
      });
    }
    return info;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds]);

  const colX = (ri) => ri * (COL_W + COL_GAP);
  // Trailing gutter so the rightmost (Final) card isn't flush against the
  // horizontal-scroll edge — flush clips its border / rounded corner, which
  // reads as the Final tile being slightly cut off. Widens the canvas, SVG
  // layer, and the sticky round-label strip together (all keyed off totalW).
  const EDGE_PAD = 24;
  const totalW =
    rounds.length * COL_W +
    Math.max(0, rounds.length - 1) * COL_GAP +
    EDGE_PAD;

  // Third-place playoff is positioned just below the Final card, in the Final
  // column, so it reads "in line" with the final rather than floating below the
  // whole (much taller) bracket tree.
  const finalMatch = rounds.length ? rounds[rounds.length - 1].matches[0] : null;
  const finalMeta = finalMatch ? layout[Number(finalMatch.match_number)] : null;
  const THIRD_LABEL_H = 22;
  // Sit the third-place block in the Semifinal column (the open gap to the LEFT
  // of the Final), vertically level with the Final card — the spot marked on the
  // bracket sheet — rather than below the Final.
  const thirdLeftCol = Math.max(0, rounds.length - 2);
  const thirdTop = finalMeta
    ? finalMeta.centerY - finalMeta.h / 2 - THIRD_LABEL_H - 6
    : 0;
  const thirdBlockBottom = thirdMatch
    ? thirdTop + THIRD_LABEL_H + 6 + PICK_CARD_H
    : 0;

  const bracketBottom = Object.values(layout).reduce(
    (mx, v) => Math.max(mx, (v.centerY || 0) + v.h / 2),
    0,
  );
  const totalH = Math.max(bracketBottom, thirdBlockBottom) + 12;

  // connectors: child right-center → mid-gap → parent center → parent left
  const connectors = useMemo(() => {
    const paths = [];
    for (let ri = 1; ri < rounds.length; ri++) {
      const prev = rounds[ri - 1].matches;
      const parentX = colX(ri);
      const childRightX = colX(ri - 1) + COL_W;
      const midX = childRightX + COL_GAP / 2;
      rounds[ri].matches.forEach((m, mi) => {
        const mn = Number(m.match_number);
        const py = layout[mn]?.centerY;
        if (py == null) return;
        let kids = prev.filter((c) => Number(c.winner_to_match_number) === mn);
        if (kids.length === 0) kids = [prev[mi * 2], prev[mi * 2 + 1]].filter(Boolean);
        kids.forEach((c) => {
          const cy = layout[Number(c.match_number)]?.centerY;
          if (cy == null) return;
          paths.push(`M ${childRightX} ${cy} H ${midX} V ${py} H ${parentX}`);
        });
      });
    }
    return paths;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds, layout]);

  const connectorColor = "rgba(148,163,184,0.55)";

  const cardSx = [
    {
      borderRadius: "12px",
      border: `1px solid ${theme.branding.gray[200]}`,
      backgroundColor: theme.branding.gray[0],
      boxSizing: "border-box",
    },
    (theme) =>
      theme.applyStyles("dark", {
        borderColor: theme.branding.darkBlue[600],
        backgroundColor: theme.branding.darkBlue[800],
      }),
  ];

  // ---- a single tappable candidate row ----
  const CandidateRow = ({ teamNum, selected, pickable, dimmed, pending, onTap }) => {
    const t = teamInfo[Number(teamNum)];
    const interactive = pickable && !isUpdating;
    const active = selected || pending; // highlight the instant it's tapped
    const eliminated = eliminatedTeams.has(Number(teamNum));
    return (
      <Grid
        container
        direction="row"
        wrap="nowrap"
        onClick={interactive ? onTap : undefined}
        sx={{
          alignItems: "center",
          gap: "8px",
          minWidth: 0,
          height: `${ROW_H}px`,
          padding: "0 8px",
          borderRadius: "8px",
          cursor: interactive ? "pointer" : "default",
          backgroundColor: active ? "#33DDEE1A" : "transparent",
          opacity: dimmed && !pending ? 0.5 : 1,
          ...(interactive
            ? {
                "&:hover": {
                  backgroundColor: active ? "#33DDEE1A" : theme.branding.gray[100],
                },
              }
            : {}),
        }}
      >
        {t?.flag ? (
          <img
            src={t.flag}
            alt=""
            style={{
              width: "22px",
              height: "14px",
              borderRadius: "2px",
              flexShrink: 0,
              opacity: eliminated ? 0.35 : 1,
              filter: eliminated ? "grayscale(1)" : "none",
            }}
          />
        ) : (
          <Grid
            sx={{
              width: "22px",
              height: "14px",
              borderRadius: "2px",
              backgroundColor: theme.branding.gray[200],
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          noWrap
          variant="subtitle2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: active ? 700 : 500,
            color: eliminated ? theme.branding.gray[400] : "inherit",
          }}
        >
          {t?.name || "TBD"}
        </Typography>
        {!!t?.worldRank && t.worldRank > 0 && (
          <Typography
            variant="caption"
            sx={{ color: theme.branding.gray[400], flexShrink: 0 }}
          >
            #{t.worldRank}
          </Typography>
        )}
        {pending ? (
          <BallSpinner size={16} />
        ) : (
          selected && (
            <CheckOutlinedIcon
              sx={{ width: "16px", height: "16px", color: theme.branding.cyan[600], flexShrink: 0 }}
            />
          )
        )}
      </Grid>
    );
  };

  // ---- a tappable "couple" option: two teams that advance together (R16+) ----
  // No world rank shown here — only the R32 matchup shows ranks. The check / busy
  // ball appears once per couple, on its first team row.
  const CoupleOption = ({ couple, selected, pending, dimmed, onTap }) => {
    const interactive = !isUpdating;
    const active = selected || pending;
    const teams = couple.teams.length ? couple.teams : [couple.rep];
    // Couple elimination display: while one team is out but its partner is
    // still alive, HIDE the loser and show only the surviving partner; only
    // once BOTH teams have lost does the couple read as dead — then show both,
    // greyed out.
    const bothEliminated =
      teams.length > 0 &&
      teams.every((tn) => eliminatedTeams.has(Number(tn)));
    const shownTeams = bothEliminated
      ? teams
      : teams.filter((tn) => !eliminatedTeams.has(Number(tn)));
    return (
      <Grid
        container
        direction="row"
        wrap="nowrap"
        onClick={interactive ? onTap : undefined}
        sx={{
          alignItems: "center",
          gap: "8px",
          minWidth: 0,
          height: `${ROW_H}px`,
          padding: "0 8px",
          borderRadius: "8px",
          cursor: interactive ? "pointer" : "default",
          backgroundColor: active ? "#33DDEE1A" : "transparent",
          opacity: dimmed && !pending ? 0.5 : 1,
          ...(interactive
            ? {
                "&:hover": {
                  backgroundColor: active ? "#33DDEE1A" : theme.branding.gray[100],
                },
              }
            : {}),
        }}
      >
        {/* flag next to each name, the two joined by "/": [flag] Name / [flag] Name */}
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{ flex: 1, minWidth: 0, alignItems: "center", gap: "6px" }}
        >
          {shownTeams.map((tn, i) => {
            const t = teamInfo[Number(tn)];
            const eliminated = bothEliminated;
            return (
              <React.Fragment key={`cpl-${couple.id}-${tn}-${i}`}>
                {i > 0 && (
                  <Typography
                    variant="subtitle2"
                    sx={{ flexShrink: 0, color: theme.branding.gray[400] }}
                  >
                    /
                  </Typography>
                )}
                {t?.flag ? (
                  <img
                    src={t.flag}
                    alt=""
                    style={{
                      width: "20px",
                      height: "13px",
                      borderRadius: "2px",
                      flexShrink: 0,
                      opacity: eliminated ? 0.35 : 1,
                      filter: eliminated ? "grayscale(1)" : "none",
                    }}
                  />
                ) : (
                  <Grid
                    sx={{
                      width: "20px",
                      height: "13px",
                      borderRadius: "2px",
                      backgroundColor: theme.branding.gray[200],
                      flexShrink: 0,
                      opacity: eliminated ? 0.35 : 1,
                    }}
                  />
                )}
                <Typography
                  noWrap
                  variant="subtitle2"
                  sx={{
                    minWidth: 0,
                    flexShrink: 1,
                    fontWeight: active ? 700 : 500,
                    color: eliminated ? theme.branding.gray[400] : "inherit",
                  }}
                >
                  {t?.name || "TBD"}
                </Typography>
              </React.Fragment>
            );
          })}
        </Grid>
        {pending ? (
          <BallSpinner size={16} />
        ) : (
          selected && (
            <CheckOutlinedIcon
              sx={{ width: "16px", height: "16px", color: theme.branding.cyan[600], flexShrink: 0 }}
            />
          )
        )}
      </Grid>
    );
  };

  // ---- one match card (R32 matchup display; couple pick for R16 → Final) ----
  const MatchCard = ({ m, ri, absolute = true }) => {
    const mn = Number(m.match_number);
    const meta = layout[mn];
    const isMatchup = ri === 0;
    const pickable = rounds[ri].def.pickable;
    const picked = picksByMatch[mn];
    const h = meta?.h || cardHFor(ri);
    const teams = isMatchup ? matchupTeams(m) : [];
    const coupleCands = isMatchup ? [] : coupleCandidatesFor(m, ri);

    return (
      <Grid
        container
        direction="column"
        wrap="nowrap"
        sx={[
          ...cardSx,
          {
            padding: `${CARD_VPAD / 2}px 6px`,
            gap: "2px",
            width: `${COL_W}px`,
            ...(absolute
              ? {
                  position: "absolute",
                  top: `${(meta?.centerY || 0) - h / 2}px`,
                  left: `${colX(ri)}px`,
                  height: `${h}px`,
                  zIndex: 1,
                }
              : { maxWidth: `${COL_W}px` }),
          },
        ]}
      >
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            height: `${CARD_HEAD}px`,
            padding: "0 6px",
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.branding.gray[400] }}>
            {pickable ? "pick winner" : "matchup"}
          </Typography>
        </Grid>

        {isMatchup ? (
          teams.map((c, idx) => (
            <CandidateRow
              key={`mt-${mn}-${c}-${idx}`}
              teamNum={c}
              selected={false}
              pickable={false}
              dimmed={false}
              pending={false}
            />
          ))
        ) : coupleCands.length === 0 ? (
          <Grid
            container
            sx={{ flex: 1, alignItems: "center", justifyContent: "center", padding: "0 6px" }}
          >
            <Typography
              variant="caption"
              sx={{ color: theme.branding.gray[400], textAlign: "center" }}
            >
              Pick the feeding matches first
            </Typography>
          </Grid>
        ) : (
          coupleCands.map((couple, idx) => (
            <React.Fragment key={`cplopt-${mn}-${couple.id}`}>
              <CoupleOption
                couple={couple}
                selected={Number(picked) === Number(couple.rep)}
                dimmed={picked != null && Number(picked) !== Number(couple.rep)}
                pending={
                  !!pendingPick &&
                  pendingPick.matchNumber === mn &&
                  Number(pendingPick.teamNumber) === Number(couple.rep)
                }
                onTap={() => onPick(mn, couple.rep)}
              />
              {/* divider between the two couple options */}
              {idx === 0 && coupleCands.length > 1 && (
                <Grid
                  sx={{
                    height: "1px",
                    backgroundColor: theme.branding.gray[200],
                    margin: "1px 8px",
                  }}
                />
              )}
            </React.Fragment>
          ))
        )}
      </Grid>
    );
  };

  // ---- third-place card body (shared shape with MatchCard) ----
  const ThirdPlaceCard = () => {
    if (!thirdMatch) return null;
    const mn = Number(thirdMatch.match_number);
    const picked = picksByMatch[mn];
    return (
      <Grid
        sx={{
          position: "absolute",
          left: `${colX(thirdLeftCol)}px`,
          top: `${thirdTop}px`,
          width: `${COL_W}px`,
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          zIndex: 1,
        }}
      >
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{ gap: "6px", alignItems: "center", height: `${THIRD_LABEL_H}px` }}
        >
          <EmojiEventsOutlinedIcon
            sx={{ height: "16px", color: theme.branding.yellow[700] }}
          />
          <Typography
            variant="caption"
            sx={{
              color: theme.branding.gray[500],
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Third place
          </Typography>
        </Grid>
        <Grid
          container
          direction="column"
          wrap="nowrap"
          sx={[
            ...cardSx,
            { padding: `${CARD_VPAD / 2}px 6px`, gap: "2px", width: `${COL_W}px` },
          ]}
        >
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{
              justifyContent: "flex-end",
              alignItems: "center",
              height: `${CARD_HEAD}px`,
              padding: "0 6px",
            }}
          >
            <Typography variant="caption" sx={{ color: theme.branding.gray[400] }}>
              pick winner
            </Typography>
          </Grid>
          {thirdCoupleCandidates.length === 0 ? (
            <Grid container sx={{ padding: "8px 6px", justifyContent: "center" }}>
              <Typography
                variant="caption"
                sx={{ color: theme.branding.gray[400], textAlign: "center" }}
              >
                Pick both semifinals first
              </Typography>
            </Grid>
          ) : (
            thirdCoupleCandidates.map((couple, idx) => (
              <React.Fragment key={`third-cpl-${couple.id}`}>
                <CoupleOption
                  couple={couple}
                  selected={Number(picked) === Number(couple.rep)}
                  dimmed={picked != null && Number(picked) !== Number(couple.rep)}
                  pending={
                    !!pendingPick &&
                    pendingPick.matchNumber === mn &&
                    Number(pendingPick.teamNumber) === Number(couple.rep)
                  }
                  onTap={() => onPick(mn, couple.rep)}
                />
                {idx === 0 && thirdCoupleCandidates.length > 1 && (
                  <Grid
                    sx={{
                      height: "1px",
                      backgroundColor: theme.branding.gray[200],
                      margin: "1px 8px",
                    }}
                  />
                )}
              </React.Fragment>
            ))
          )}
        </Grid>
      </Grid>
    );
  };

  const ready = !_.isEmpty(rounds);

  // Track whether the bracket can still scroll right, to drive the right-edge
  // "more →" cue. The round labels now live INSIDE the scroller (drawn as the
  // bracket's top row), so they scroll with the columns natively — no transform
  // sync needed, and a label can never drift off its column.
  useEffect(() => {
    const scroller = bracketScrollRef.current;
    if (!scroller) return undefined;
    const sync = () => {
      setCanScrollRight(
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1,
      );
    };
    sync();
    scroller.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    return () => {
      scroller.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, [ready, totalW]);

  return (
    <Grid
      container
      direction="column"
      sx={{ width: "100%", justifyContent: "center" }}
    >
      {(isKnockout || picksProgress?.groupStageComplete) && (
        <ContentWrapper>
          <Spinner show={statusLoading || picksLoading} />
          <Grid
            container
            direction="column"
            sx={{ gap: "20px", alignItems: "center", width: "100%" }}
          >
            {/* sticky title row */}
            <Grid
              container
              direction="column"
              sx={[
                {
                  position: "sticky",
                  top: `${stickyTopOffset}px`,
                  zIndex: 55,
                  backgroundColor: theme.branding.gray[0],
                  gap: isMobile ? "8px" : "14px",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  maxWidth: "calc(100vw - 48px)",
                  paddingBottom: "6px",
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid
                container
                direction={isMobile ? "column" : "row"}
                wrap="nowrap"
                sx={{
                  width: "100%",
                  gap: isMobile ? "6px" : "12px",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "flex-start" : "center",
                }}
              >
                <Grid
                  container
                  direction="column"
                  wrap="nowrap"
                  sx={{ width: "auto", gap: "2px", alignItems: "flex-start" }}
                >
                  <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{ letterSpacing: "0.4px" }}
                  >
                    Knockout Bracket Picks
                  </Typography>
                  <Grid
                    container
                    direction={isMobile ? "column" : "row"}
                    wrap="nowrap"
                    sx={{
                      gap: isMobile ? "2px" : "14px",
                      justifyContent: "flex-start",
                      alignItems: isMobile ? "flex-start" : "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={[
                        { fontWeight: 500 },
                        (theme) =>
                          theme.applyStyles("dark", {
                            color: theme.branding.gray[400],
                          }),
                      ]}
                    >
                      {isMobile
                        ? "Tap to pick each winner — start at the Round of 16"
                        : "Tap a team to pick the winner of each match — start at the Round of 16"}
                    </Typography>
                    {!isMobile && (
                      <Typography
                        variant="caption"
                        sx={{ color: theme.branding.gray[400] }}
                      >
                        # = FIFA World Rank
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                {tabs?.length > 1 && (
                  <Grid container sx={{ width: "auto", flexShrink: 0 }}>
                    <TabSwitch
                      tabs={tabs}
                      currTab={currTab}
                      handleTabChange={handleTabChange}
                      context="picks"
                    />
                  </Grid>
                )}
              </Grid>

            </Grid>

            {!ready ? (
              <Grid
                container
                sx={{ padding: "64px 16px", justifyContent: "center" }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: theme.branding.gray[400] }}
                >
                  Loading bracket…
                </Typography>
              </Grid>
            ) : (
              <Grid
                container
                direction="column"
                sx={{ position: "relative", width: "100%", gap: "20px" }}
              >
                {/* the bracket — scrolls horizontally; the third-place card is
                    positioned inside this same canvas, under the Final column. */}
                <Grid
                  ref={bracketScrollRef}
                  sx={{ width: "100%", overflowX: "auto", paddingBottom: "8px" }}
                >
                  <Grid
                    sx={{
                      position: "relative",
                      width: `${totalW}px`,
                      height: `${totalH}px`,
                      minWidth: "100%",
                    }}
                  >
                    {/* round labels — drawn as the top row of the canvas so they
                        scroll horizontally in lockstep with the columns (always
                        aligned), filling the HEADER_H band the layout reserves */}
                    {rounds.map((round, ri) => (
                      <Typography
                        key={`hdr-${round.def.label}`}
                        variant="caption"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: `${colX(ri)}px`,
                          width: `${COL_W}px`,
                          textAlign: "center",
                          color: theme.branding.cyan[600],
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          zIndex: 2,
                        }}
                      >
                        {round.def.label}
                      </Typography>
                    ))}

                    <svg
                      width={totalW}
                      height={totalH}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        pointerEvents: "none",
                        overflow: "visible",
                      }}
                    >
                      {connectors.map((d, i) => (
                        <path
                          key={i}
                          d={d}
                          fill="none"
                          stroke={connectorColor}
                          strokeWidth="1.5"
                        />
                      ))}
                    </svg>

                    {rounds.map((round, ri) =>
                      round.matches.map((m) => (
                        <MatchCard key={`ko-${m.match_number}`} m={m} ri={ri} />
                      )),
                    )}

                    {/* third-place playoff — under the Final, picked from SF losers */}
                    <ThirdPlaceCard />
                  </Grid>
                </Grid>
                {/* right-edge fade + chevron: signals the bracket scrolls to
                    more rounds; hidden once scrolled to the end */}
                {canScrollRight && (
                  <Box
                    sx={[
                      {
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: "8px",
                        width: "48px",
                        pointerEvents: "none",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-end",
                        paddingTop: "70px",
                        background: `linear-gradient(to right, transparent, ${theme.branding.gray[0]})`,
                      },
                      (theme) =>
                        theme.applyStyles("dark", {
                          background: `linear-gradient(to right, transparent, ${theme.branding.darkBlue[800]})`,
                        }),
                    ]}
                  >
                    <KeyboardArrowRightIcon
                      sx={{ color: theme.branding.cyan[600] }}
                    />
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </ContentWrapper>
      )}
    </Grid>
  );
};

export default KnockoutPicks;
